/**
 * Service HTTP para Carta Porte
 * Sprint D — HU-D2
 *
 * Centraliza las llamadas HTTP a los endpoints de Carta Porte del API.
 */

import { apiClient } from './api';
import { toFiscalUppercase } from '../utils/fiscalText';
import type {
    FiscalData,
    FiscalDataResponse,
    CsdInfo,
    CsdListResponse,
    CsdUploadResponse,
    TimbreProfile,
    TimbreProfileResponse,
    CartaPorteConnectionStatus,
    CartaPorteConnectionStatusResponse,
    CartaPorteItem,
    CartaPorteItemResponse,
    CartaPorteListResponse,
    CartaPorteDetail,
    CartaPorteDetailResponse,
    PreflightCheck,
    PreflightResult,
    PreflightResponse,
    StampIngresoDryRunResponse,
    StampIngresoReadiness,
    StampIngresoReadinessResponse,
    StampIngresoRequest,
    StampIngresoResult,
    StampIngresoResponse,
    SatCatalogItem,
    CartaPorteSatCatalogResponse,
    PostalDistanceResponse,
} from '../types/cartaPorte';

function normalizeSatCatalogItems(payload: unknown): SatCatalogItem[] {
    if (!Array.isArray(payload)) {
        return [];
    }

    return payload.flatMap((item) => {
        if (!item || typeof item !== 'object') {
            return [];
        }

        const record = item as Record<string, unknown>;
        const clave = String(record.clave ?? '').trim();
        const nombre = String(record.nombre ?? '').trim();
        const descripcion = String(record.descripcion ?? nombre).trim();

        if (!clave || !descripcion) {
            return [];
        }

        return [{
            clave,
            descripcion,
            nombre: nombre || undefined,
            simbolo: String(record.simbolo ?? '').trim() || undefined,
            bandera: String(record.bandera ?? '').trim() || undefined,
        }];
    });
}

function normalizePreflightResult(result: unknown): PreflightResult {
    if (!result || typeof result !== 'object') {
        return { ready_to_stamp: false, checks: [] };
    }

    const record = result as Record<string, unknown>;
    const checks = Array.isArray(record.checks)
        ? record.checks
            .filter((check): check is PreflightCheck =>
                Boolean(
                    check
                    && typeof check === 'object'
                    && 'check' in check
                    && 'passed' in check
                    && 'message' in check,
                ),
            )
        : [];

    if (checks.length > 0) {
        return {
            ready_to_stamp: Boolean(record.ready_to_stamp),
            checks,
            csd_status: record.csd_status && typeof record.csd_status === 'object'
                ? record.csd_status as PreflightResult['csd_status']
                : undefined,
        };
    }

    const legacyChecks = record.checks && typeof record.checks === 'object'
        ? Object.entries(record.checks as Record<string, unknown>).map(([check, status]) => ({
            check,
            passed: status === 'ok',
            message: check,
        }))
        : [];

    return {
        ready_to_stamp: legacyChecks.every((check) => check.passed),
        checks: legacyChecks,
    };
}

function normalizeStringField(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

function normalizeFiscalDataRecord(payload: unknown): FiscalData | null {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const record = payload as Record<string, unknown>;

    return {
        rfc: toFiscalUppercase(normalizeStringField(record.rfc)),
        razon_social: toFiscalUppercase(normalizeStringField(record.razon_social)),
        regimen_fiscal: normalizeStringField(record.regimen_fiscal),
        codigo_postal_fiscal: normalizeStringField(record.codigo_postal_fiscal),
        fiscal_locked: Boolean(record.fiscal_locked),
    };
}

// ── Estado / conexión ──

export async function getConnectionStatus(): Promise<CartaPorteConnectionStatus> {
    const response = await apiClient.get<CartaPorteConnectionStatusResponse>('/api/carta-porte/status');
    return response.data;
}

export async function connectAccount(): Promise<CartaPorteConnectionStatus> {
    const response = await apiClient.post<CartaPorteConnectionStatusResponse>('/api/carta-porte/connect', {});
    return response.data;
}

// ── Datos Fiscales ──

export async function getFiscalData(): Promise<FiscalData | null> {
    try {
        const response = await apiClient.get<FiscalDataResponse>('/api/carta-porte/fiscal');
        return normalizeFiscalDataRecord(response.data);
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'message' in error) {
            const apiError = error as { message: string; status?: number };
            if (apiError.status === 404 || apiError.message.includes('404')) {
                return null;
            }
        }
        throw error;
    }
}

export async function setFiscalData(data: FiscalData): Promise<FiscalData> {
    const response = await apiClient.put<FiscalDataResponse>('/api/carta-porte/fiscal', data);
    return normalizeFiscalDataRecord(response.data) ?? {
        rfc: '',
        razon_social: '',
        regimen_fiscal: '',
        codigo_postal_fiscal: '',
        fiscal_locked: false,
    };
}

// ── CSD ──

export async function listCsds(): Promise<CsdInfo[]> {
    const response = await apiClient.get<CsdListResponse>('/api/carta-porte/csd');
    return response.data;
}

export async function uploadCsd(formData: FormData): Promise<CsdInfo> {
    const response = await apiClient.postForm<CsdUploadResponse>('/api/carta-porte/csd', formData);
    return response.data;
}

// ── Timbres ──

export async function getTimbreProfile(): Promise<TimbreProfile> {
    const response = await apiClient.get<TimbreProfileResponse>('/api/carta-porte/timbres');
    return response.data;
}

// ── Carta Porte CRUD ──

export async function createFromContract(contractId: number): Promise<CartaPorteItem> {
    const response = await apiClient.post<CartaPorteItemResponse>(
        `/api/carta-porte/from-contract/${contractId}`,
        {},
    );
    return response.data;
}

interface ListCartasPorteParams {
    page?: number;
    limit?: number;
    status?: string;
}

export async function listCartasPorte(params?: ListCartasPorteParams): Promise<{
    items: CartaPorteItem[];
    meta?: CartaPorteListResponse['meta'];
}> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);

    const qs = query.toString();
    const endpoint = `/api/carta-porte${qs ? `?${qs}` : ''}`;

    const response = await apiClient.get<CartaPorteListResponse>(endpoint);
    return { items: response.data, meta: response.meta };
}

export async function getCartaPorteDetail(id: number): Promise<CartaPorteDetail> {
    const response = await apiClient.get<CartaPorteDetailResponse>(`/api/carta-porte/${id}`);
    return response.data;
}

// ── Timbrado ──

export async function preflightStamp(id: number): Promise<PreflightResult> {
    const response = await apiClient.post<PreflightResponse>(
        `/api/carta-porte/${id}/preflight`,
        {},
    );
    return normalizePreflightResult(response.data);
}

export async function stampCartaPorte(id: number): Promise<CartaPorteItem> {
    const response = await apiClient.post<CartaPorteItemResponse>(
        `/api/carta-porte/${id}/stamp`,
        {},
    );
    return response.data;
}

export async function getStampIngresoReadiness(contractId: number): Promise<StampIngresoReadiness> {
    const response = await apiClient.get<StampIngresoReadinessResponse>(
        `/api/carta-porte/contracts/${contractId}/stamp-ingreso/readiness`,
    );

    return response.data;
}

export async function dryRunStampIngreso(id: number, payload: StampIngresoRequest) {
    const response = await apiClient.post<StampIngresoDryRunResponse>(
        `/api/carta-porte/${id}/stamp-ingreso/dry-run`,
        payload,
    );

    return response.data;
}

export async function stampIngreso(id: number, payload: StampIngresoRequest): Promise<{
    cartaPorte: CartaPorteItem;
    stampResult: StampIngresoResult;
}> {
    const response = await apiClient.post<StampIngresoResponse>(
        `/api/carta-porte/${id}/stamp-ingreso`,
        payload,
    );

    return {
        cartaPorte: response.data.carta_porte,
        stampResult: response.data.stamp_result,
    };
}

// ── Descargas ──

export async function downloadXml(id: number): Promise<Blob> {
    const { blob } = await apiClient.getBlob(`/api/carta-porte/${id}/xml`);
    return blob;
}

export async function downloadPdf(id: number): Promise<Blob> {
    const { blob } = await apiClient.getBlob(`/api/carta-porte/${id}/pdf`);
    return blob;
}

// ── Catálogos remotos ──

export async function searchProductosSat(q: string, limit?: number): Promise<SatCatalogItem[]> {
    const query = new URLSearchParams({ q });
    if (limit) query.set('limit', String(limit));
    const response = await apiClient.get<CartaPorteSatCatalogResponse>(
        `/api/carta-porte/catalogs/productos?${query.toString()}`,
    );
    return normalizeSatCatalogItems(response.data);
}

export async function searchUnidadesSat(q: string, limit?: number): Promise<SatCatalogItem[]> {
    const query = new URLSearchParams({ q });
    if (limit) query.set('limit', String(limit));
    const response = await apiClient.get<CartaPorteSatCatalogResponse>(
        `/api/carta-porte/catalogs/unidades?${query.toString()}`,
    );
    return normalizeSatCatalogItems(response.data);
}

export async function searchMaterialesPeligrosos(q: string, limit?: number): Promise<SatCatalogItem[]> {
    const query = new URLSearchParams({ q });
    if (limit) query.set('limit', String(limit));
    const response = await apiClient.get<CartaPorteSatCatalogResponse>(
        `/api/carta-porte/catalogs/materiales-peligrosos?${query.toString()}`,
    );
    return normalizeSatCatalogItems(response.data);
}

export async function searchEmbalajes(q: string, limit?: number): Promise<SatCatalogItem[]> {
    const query = new URLSearchParams({ q });
    if (limit) query.set('limit', String(limit));
    const response = await apiClient.get<CartaPorteSatCatalogResponse>(
        `/api/carta-porte/catalogs/embalajes?${query.toString()}`,
    );
    return normalizeSatCatalogItems(response.data);
}

export async function getDistanciaPostal(originCp: string, destCp: string): Promise<number> {
    const query = new URLSearchParams({ cp_origen: originCp, cp_destino: destCp });
    const response = await apiClient.get<PostalDistanceResponse>(
        `/api/carta-porte/catalogs/postal/distancia?${query.toString()}`,
    );
    return response.data.distancia;
}

export const cartaPorteService = {
    getConnectionStatus,
    connectAccount,
    getFiscalData,
    setFiscalData,
    listCsds,
    uploadCsd,
    getTimbreProfile,
    createFromContract,
    listCartasPorte,
    getCartaPorteDetail,
    preflightStamp,
    stampCartaPorte,
    getStampIngresoReadiness,
    dryRunStampIngreso,
    stampIngreso,
    downloadXml,
    downloadPdf,
    searchProductosSat,
    searchUnidadesSat,
    searchMaterialesPeligrosos,
    searchEmbalajes,
    getDistanciaPostal,
};
